import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../../i18n";
import { Blattvonunten } from "../../bausteine/Blattvonunten";
import { Abschnitt, Leersatz, Zeilenblatt, Zweizeiler } from "../../bausteine/Handyformen";
import { FehlerBand } from "../../bausteine";
import { Zeichen } from "../../bausteine/Zeichen";
import { Kundensuche } from "./Kundensuche";
import { OrtAnlegen } from "../../bausteine/OrtAnlegen";
import { KundeFormular } from "../../bausteine/KundeFormular";
import { useKataloge, useKunde, useLeistungsarten } from "../../daten/haken";
import { useAufnahmeSpontan } from "../../daten/ortsbegehung";
import type { Kunde } from "../../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Ein Aufmaß, das sich der Vertriebler selbst anlegt — 21.09.2026.

   Luca: *„Kann ich dann nicht auch direkt vor Ort ein Aufmaß anlegen,
   sodass ich dann die Möglichkeit habe, spontan direkt ein Aufmaß zu
   machen und dann auch daraus das Angebot zu erzeugen für einen
   Kunden?"* — *„Trage ich da die Kunden ein, wenn es sie nicht gibt,
   innerhalb dieses Prozesses, aber das alles in einem Schritt, sodass
   dann der Außendienst das alles selbstständig schnell machen kann?"*

   ── Drei Fragen, und die dritte ist freiwillig ────────────────────

   ```
   WER?          [ Kunde suchen … ]        → oder neuer Interessent
   WOFÜR?        ○ PV-Reinigung  ○ Mulchen
   WO?           ○ Feld Süd  ○ Feld Nord   → darf offen bleiben
   ────────────────────────────────────────
   [      Aufmaß anlegen und loslegen      ]
   ```

   **Der Ort darf fehlen, der Kunde nicht.** Beim Neukunden gibt es die
   Baustelle noch nicht (19-ortsaufnahme.md, `[4]`) — ohne Kunde
   dagegen gehörte die Zahl niemandem, und beim nächsten Durchgang
   fände sie keiner wieder.

   **Wen es noch nicht gibt, legt dieses Blatt an** — als Interessent,
   mit **dem** Kundenformular (`bausteine/KundeFormular.tsx`, Form
   `blatt`): dasselbe, das am Schreibtisch, im Deckblatt und auf
   `/kunden/neu` steht, mit Anschrift, Ansprechpartner, Telefon,
   E-Mail und den Pflichtfeldern des Betriebs (0098).

   Bis zum 21.09.2026 stand hier `InteressentSchnell` mit drei Feldern
   — Luca: *„Jetzt hast du in Mobile ja quasi zwei
   Interessentenanlagen? … das muss optimiert werden und shared
   sein."* Waren es: Wer so einen Interessenten anlegte, hatte keine
   Strasse und keine PLZ, und das Angebot an ihn ging mit einer halben
   Anschrift hinaus.

   **Danach steht man in der Begehung, nicht in einer Liste.** Das ist
   der ganze Punkt (19-ortsaufnahme.md, 15.09.2026): *„Wer sie sich
   selbst anlegt und gleich erledigt, soll in einem Zug durchkommen —
   anlegen, aufmessen, fertig, ohne Umweg über eine Liste, in der sie
   kurz auftaucht."*

   ── Was hier NICHT gefragt wird ───────────────────────────────────

   **Kein Datum.** Eine Aufnahme hat keins — das ist ihr Wesen. Wer
   hier steht, ist schon da.

   **Kein Aufmaß-Kennzeichen.** `needs_site_survey` ist der Auslöser
   für die Bestellung aus einem Angebot heraus. Wer freiwillig
   hinfährt und misst, braucht keinen — deshalb stehen hier **alle**
   aktiven Leistungsarten zur Wahl und nicht nur die mit Haken.
   ══════════════════════════════════════════════════════════════════ */

export function AufmassAnlegen({ offen, schliessen }: {
  offen: boolean;
  schliessen: () => void;
}) {
  const t = useT();
  const gehZu = useNavigate();
  const anlegen = useAufnahmeSpontan();
  const { data: leistungen } = useLeistungsarten();
  const kataloge = useKataloge();

  const [wort, setzeWort] = useState("");
  const [kunde, setzeKunde] = useState<Kunde | null>(null);
  const [neuerInteressent, setzeNeu] = useState(false);
  const [leistungId, setzeLeistung] = useState("");
  const [ort, setzeOrt] = useState("");
  /* **Die Baustelle entsteht hier, wenn es sie noch nicht gibt**
     (21.09.2026). Luca: *„Ich will auch, wenn ich schon Kunden ausgewählt
     habe und da keine Baustelle gibt, die Baustellen noch anlegen
     können."*

     Derselbe Baustein wie in der Schnellerfassung und im Angebot
     (`bausteine/OrtAnlegen.tsx`) — am Telefon kommt er ohnehin von unten.
     Ein zweites Ortsformular wäre dasselbe Problem wie eben beim Kunden. */
  const [ortAnlegen, setzeOrtAnlegen] = useState(false);

  /* Die Akte des gewählten Kunden — sie bringt die Einsatzorte mit. Ohne
     Kunde wird nicht gefragt: Eine Ortsliste ohne Kunden gibt es nicht. */
  const akte = useKunde(kunde?.nr ?? "");
  const orte = akte.data?.orte ?? [];

  /* **Die Einheit, nicht ihre Kennung.** `l.groesse` ist `QM` — im Blatt
     stand damit „Aufmaß in QM", und das liest niemand als Quadratmeter.
     Das Wort dazu steht im Katalog der Zählgrößen, denselben liest der
     Preisstamm (`kataloge.zaehlgroessen`); eine zweite Übersetzung daneben
     liefe von ihm weg. Fehlt der Eintrag, bleibt die Kennung stehen — ein
     leeres Wort wäre schlimmer. */
  const einheitVon = (code: string): string =>
    (kataloge.data?.zaehlgroessen ?? []).find((g) => g.code === code)?.einheit || code;

  /* **Nur, wofür ein Aufmaß gebraucht wird** (Luca, 21.09.2026): *„Ich will
     auch einstellen können, wofür wir überhaupt den Aufmaß brauchen … Wir
     haben zum Beispiel bei Mulchen oder bei Bedienung den Haken gar nicht
     gesetzt bei ‚braucht eine Ortsaufnahme'. Also will ich das auch gar
     nicht auswählen können."*

     Der Haken heißt `needs_site_survey` und steht im Preisstamm an der
     Leistungsart. Bis hierher stand hier **jede aktive** — mit der
     Begründung, wer freiwillig hinfährt, brauche keinen Auslöser. Die
     Begründung war falsch herum gedacht: Der Haken ist nicht nur ein
     Auslöser, er sagt auch, **wofür Messen überhaupt einen Sinn hat**. Eine
     Fahrerstellung je Stunde misst niemand. */
  const waehlbar = (leistungen?.zeilen ?? []).filter((l) => l.aktiv && l.ortsaufnahme);
  const bereit = Boolean(kunde && leistungId);

  const zu = () => {
    setzeWort(""); setzeKunde(null); setzeNeu(false);
    setzeLeistung(""); setzeOrt(""); setzeOrtAnlegen(false);
    schliessen();
  };

  const los = () => {
    if (!kunde || !leistungId) return;
    anlegen.mutate(
      { kundeNr: kunde.nr, leistungsartId: leistungId, ...(ort ? { ort } : {}) },
      {
        onSuccess: (b) => {
          zu();
          // Direkt in die Begehung — nicht in die Liste, aus der man sie
          // dann heraussuchen müsste.
          void gehZu({ to: "/aufnahmen/$id", params: { id: b.id } });
        },
      },
    );
  };

  /* ── Der Kunde ist noch nicht gewählt: suchen oder anlegen ──
     Derselbe Aufbau wie im Angebot am Telefon — Liste als Weg, Suchfeld
     als Abkürzung, „Neuer Interessent" im Fuß. */
  if (offen && !kunde) {
    return (
      <Blattvonunten
        offen={offen}
        schliessen={zu}
        form="unten"
        rast={neuerInteressent ? "gross" : "klein"}
        titel={t(neuerInteressent ? "aufmassAnlegen.interessentTitel" : "aufmassAnlegen.werTitel")}
        {...(neuerInteressent ? { zurueck: () => setzeNeu(false) } : {})}
      >
        {neuerInteressent ? (
          /* **Dasselbe Formular wie am Schreibtisch** (21.09.2026, Lucas
             Ansage: *„das muss optimiert werden und shared sein"*).
             Anschrift, Ansprechpartner, Telefon, E-Mail — und die
             Pflichtfelder, die der Betrieb eingestellt hat (0098). Ein
             Interessent, der nur Firma und Ort trägt, geht mit einer halben
             Anschrift auf ein Angebot. */
          <KundeFormular
            form="blatt"
            art="interessent"
            abbrechen={() => setzeNeu(false)}
            {...(wort.trim() ? { vorbelegt: { firma: wort.trim() } } : {})}
            angelegt={(k) => { setzeKunde(k); setzeNeu(false); }}
          />
        ) : (
          <>
            <Kundensuche
              wort={wort}
              wortAendern={setzeWort}
              waehlen={setzeKunde}
              kopfzeile={t("aufmassAnlegen.zuletzt")}
            />
            {/* **Der Weg zum Neukunden steht unter der Liste**, nicht
                daneben: Erst suchen, dann feststellen, dass es ihn nicht
                gibt — in dieser Reihenfolge sucht man. */}
            <button type="button" className="ob-leise" onClick={() => setzeNeu(true)}>
              <span>{t("aufmassAnlegen.neuerInteressent")}</span>
            </button>
          </>
        )}
      </Blattvonunten>
    );
  }

  return (
    <Blattvonunten
      offen={offen}
      schliessen={zu}
      form="unten"
      rast="gross"
      titel={t("aufmassAnlegen.titel")}
      unter={kunde?.name}
      zurueck={() => setzeKunde(null)}
      fuss={(
        <button
          type="button"
          className="handy-knopf"
          disabled={!bereit || anlegen.isPending}
          onClick={los}
        >
          {t(anlegen.isPending ? "aufmassAnlegen.laeuft"
            : bereit ? "aufmassAnlegen.los" : "aufmassAnlegen.erstLeistung")}
        </button>
      )}
    >
      {anlegen.isError ? (
        <FehlerBand fehler={anlegen.error} />
      ) : null}

      {/* **Wofür** — alle aktiven Leistungsarten, nicht nur die mit
          Aufmaß-Kennzeichen: Wer freiwillig hinfährt, braucht keinen
          Auslöser. Was gemessen wird, sagt die Zählgröße daneben. */}
      <Abschnitt titel={t("aufmassAnlegen.wofuer")}>
        {waehlbar.length ? (
          <Zeilenblatt>
            {waehlbar.map((l) => {
              const dran = leistungId === l.id;
              /* **Der Haken rechts trägt die Wahl für das Auge**,
                 `aria-pressed` für den Vorleser (`Handyformen.tsx`,
                 `gedrueckt`). Ohne ihn war die Zeile angetippt und sah aus
                 wie vorher — Luca, 21.09.2026: *„Dann funktioniert das
                 Auswählen auch nicht sauber. Da wird nichts markiert."*
                 Dieselbe Form wie die Bestätigung im Abschluss der
                 Sammlung (`MerklisteAbschluss.tsx`). */
              return (
                <Zweizeiler
                  key={l.id}
                  titel={l.label}
                  unter={t("aufnahmenTelefon.misstKurz", { einheit: einheitVon(l.groesse) })}
                  gedrueckt={dran}
                  punkt={dran ? "gut" : "ruhig"}
                  winkel={false}
                  beiKlick={() => setzeLeistung(dran ? "" : l.id)}
                  {...(dran ? { rechts: <Zeichen name="haken" /> } : {})}
                />
              );
            })}
          </Zeilenblatt>
        ) : (
          /* **Kein Haken gesetzt heißt: hier gibt es nichts zu messen.**
             Der Satz schickt dorthin, wo er gesetzt wird — sonst steht
             jemand vor einer leeren Liste und hält sie für einen Fehler. */
          <Leersatz>{t("aufmassAnlegen.keineLeistung")}</Leersatz>
        )}
      </Abschnitt>

      {/* **Wo** — freiwillig. Beim Neukunden gibt es die Baustelle noch
          nicht; dann bekommt nur die Aufnahme die Zahl, und der Ort
          entsteht später. Der Abschnitt fehlt ganz, wo es keinen gibt:
          eine leere Liste mit einer Überschrift ist eine Frage ohne
          Antwort. */}
      {orte.length ? (
        <Abschnitt
          titel={t("aufmassAnlegen.wo")}
          auskunft={ort ? undefined : t("aufmassAnlegen.woOffen")}
        >
          <Zeilenblatt>
            {orte.map((o) => {
              const dran = ort === o.name;
              return (
                <Zweizeiler
                  key={o.uuid ?? o.id ?? o.name}
                  titel={o.name}
                  unter={[o.ort, o.wo].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}
                  gedrueckt={dran}
                  punkt={dran ? "gut" : "ruhig"}
                  winkel={false}
                  beiKlick={() => setzeOrt(dran ? "" : o.name)}
                  {...(dran ? { rechts: <Zeichen name="haken" /> } : {})}
                />
              );
            })}
          </Zeilenblatt>
          {/* **Auch wenn es Orte gibt, kann eine Baustelle dazukommen** —
              eine Anlage hat selten nur eine Fläche. Der Weg steht unter
              der Liste, wie „Kunde ist noch nicht angelegt" unter den
              Kunden: erst sehen, was da ist, dann feststellen, dass es
              fehlt. */}
          <button type="button" className="ob-leise" onClick={() => setzeOrtAnlegen(true)}>
            <Zeichen name="plus" />
            <span>{t("aufmassAnlegen.ortDazu")}</span>
          </button>
        </Abschnitt>
      ) : (
        <Abschnitt titel={t("aufmassAnlegen.wo")}>
          <Leersatz>{t("aufmassAnlegen.ohneOrt")}</Leersatz>
          <button type="button" className="ob-leise" onClick={() => setzeOrtAnlegen(true)}>
            <Zeichen name="plus" />
            <span>{t("aufmassAnlegen.ortAnlegen")}</span>
          </button>
        </Abschnitt>
      )}

      {/* Das Blatt darüber: Es legt den Ort in der **Akte** an — nicht nur
          im Speicher dieser Maske. Danach steht er gewählt da, und beim
          nächsten Durchgang ist er schon vorbelegt (Regel 11). */}
      <OrtAnlegen
        offen={ortAnlegen}
        kunde={kunde}
        erster={orte.length === 0}
        schliessen={() => setzeOrtAnlegen(false)}
        angelegt={(o) => {
          setzeOrt(o.name);
          setzeOrtAnlegen(false);
          void akte.refetch();
        }}
      />
    </Blattvonunten>
  );
}
